import { createShiftJsonClient } from '../http/shift-json-client';
import type { ShiftStaffingDemandPatch, ShiftStaffingDemandWeek, ShiftSchedulePublicationPatch, ShiftWeekdayPriorityPatch } from 'shared-models';
import type {
  ShiftScheduleAssignSlotEmployeeInput,
  ShiftScheduleSlotCandidatesResponse,
  ShiftScheduleCreateRoleInput,
  ShiftScheduleCreateSectionInput,
  ShiftScheduleCreateShiftInput,
  ShiftScheduleCreateSlotInput,
  ShiftScheduleRole,
  ShiftScheduleSection,
  ShiftScheduleShift,
  ShiftScheduleSlot,
  ShiftScheduleUpdateRoleInput,
  ShiftScheduleUpdateSectionInput,
  ShiftScheduleUpdateShiftInput,
  ShiftScheduleUpdateSlotInput,
  ShiftScheduleWeek,
  ShiftScheduleEmployeeInsight,
} from '../../models/schedule.types';
import type { ShiftStaffingPlan, ShiftStaffingPlanApply } from 'shared-models';

const requestJson = createShiftJsonClient('אין תקשורת לשרת. ודאו שה-backend רץ על http://localhost:5000.');

class ShiftScheduleService {
  async patchPublication(weekStart: string, payload: ShiftSchedulePublicationPatch): Promise<void> {
    return requestJson(`/shift/schedule/publication?weekStart=${encodeURIComponent(weekStart)}`, {
      method: 'PATCH', body: JSON.stringify(payload),
    });
  }

  async getStaffingDemand(weekStart: string, offset = 0): Promise<ShiftStaffingDemandWeek> {
    return requestJson(`/shift/schedule/staffing-demand?weekStart=${encodeURIComponent(weekStart)}&offset=${offset}&limit=25`, { method: 'GET' });
  }

  async patchStaffingDemand(payload: ShiftStaffingDemandPatch): Promise<void> {
    return requestJson('/shift/schedule/staffing-demand', { method: 'PATCH', body: JSON.stringify(payload) });
  }

  async patchWeekdayPriorities(payload: ShiftWeekdayPriorityPatch): Promise<void> {
    return requestJson('/shift/schedule/weekday-priorities', { method: 'PATCH', body: JSON.stringify(payload) });
  }

  async getStaffingPlan(weekStart: string): Promise<ShiftStaffingPlan> {
    return requestJson(`/shift/schedule/staffing-plan?weekStart=${encodeURIComponent(weekStart)}`, { method: 'GET' });
  }

  async applyStaffingPlan(payload: ShiftStaffingPlanApply): Promise<void> {
    return requestJson('/shift/schedule/staffing-plan', { method: 'PATCH', body: JSON.stringify(payload) });
  }

  async getWeek(weekStart: string): Promise<ShiftScheduleWeek> {
    return requestJson<ShiftScheduleWeek>(`/shift/schedule?weekStart=${encodeURIComponent(weekStart)}`, {
      method: 'GET',
    });
  }

  async getEmployeeInsight(employeePk: number, weekStart: string): Promise<ShiftScheduleEmployeeInsight> {
    return requestJson<ShiftScheduleEmployeeInsight>(
      `/shift/schedule/employee-insights/${employeePk}?weekStart=${encodeURIComponent(weekStart)}`,
      { method: 'GET' },
    );
  }

  async createSection(payload: ShiftScheduleCreateSectionInput): Promise<ShiftScheduleSection> {
    return requestJson<ShiftScheduleSection>('/shift/schedule/sections', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSection(
    sectionId: number,
    payload: ShiftScheduleUpdateSectionInput,
  ): Promise<ShiftScheduleSection> {
    return requestJson<ShiftScheduleSection>(`/shift/schedule/sections/${sectionId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteSection(sectionId: number): Promise<{ ok: true }> {
    return requestJson<{ ok: true }>(`/shift/schedule/sections/${sectionId}`, {
      method: 'DELETE',
    });
  }

  async createShift(payload: ShiftScheduleCreateShiftInput): Promise<ShiftScheduleShift> {
    return requestJson<ShiftScheduleShift>('/shift/schedule/shifts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateShift(shiftId: number, payload: ShiftScheduleUpdateShiftInput): Promise<ShiftScheduleShift> {
    return requestJson<ShiftScheduleShift>(`/shift/schedule/shifts/${shiftId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteShift(shiftId: number): Promise<{ ok: true }> {
    return requestJson<{ ok: true }>(`/shift/schedule/shifts/${shiftId}`, {
      method: 'DELETE',
    });
  }

  async createRole(payload: ShiftScheduleCreateRoleInput): Promise<ShiftScheduleRole> {
    return requestJson<ShiftScheduleRole>('/shift/schedule/roles', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateRole(roleId: number, payload: ShiftScheduleUpdateRoleInput): Promise<ShiftScheduleRole> {
    return requestJson<ShiftScheduleRole>(`/shift/schedule/roles/${roleId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteRole(roleId: number): Promise<{ ok: true }> {
    return requestJson<{ ok: true }>(`/shift/schedule/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async createSlot(payload: ShiftScheduleCreateSlotInput): Promise<ShiftScheduleSlot> {
    return requestJson<ShiftScheduleSlot>('/shift/schedule/slots', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSlot(slotId: number, payload: ShiftScheduleUpdateSlotInput): Promise<ShiftScheduleSlot> {
    return requestJson<ShiftScheduleSlot>(`/shift/schedule/slots/${slotId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getSlotCandidates(slotId: number): Promise<ShiftScheduleSlotCandidatesResponse> {
    return requestJson<ShiftScheduleSlotCandidatesResponse>(`/shift/schedule/slots/${slotId}/candidates`, {
      method: 'GET',
    });
  }

  async assignSlotEmployee(
    slotId: number,
    payload: ShiftScheduleAssignSlotEmployeeInput,
  ): Promise<ShiftScheduleSlot> {
    return requestJson<ShiftScheduleSlot>(`/shift/schedule/slots/${slotId}/employee`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteSlot(slotId: number): Promise<{ ok: true }> {
    return requestJson<{ ok: true }>(`/shift/schedule/slots/${slotId}`, {
      method: 'DELETE',
    });
  }
}

export const shiftScheduleService = new ShiftScheduleService();
